import { Link, useParams } from "react-router-dom";
import MarketplaceHeader from "../components/MarketplaceHeader";
import Footer from "../components/Footer";
import FloatingTools from "../components/FloatingTools";

const pages = {
  "about": {
    eyebrow: "About TAZY CART",
    title: "A marketplace built for buyers, sellers and delivery partners.",
    body: "TAZY CART helps customers find products, connect with verified sellers, send buying requests, place orders and work with registered transporters from one digital marketplace.",
    cards: ["Live product discovery", "Verified sellers", "Transporter marketplace", "Business operations tools"],
    cta: { label: "Explore marketplace", to: "/" },
  },
  "marketplace-rules": {
    eyebrow: "Marketplace rules",
    title: "Clear controls for safe and responsible trading.",
    body: "Sellers must provide accurate product information, avoid prohibited goods, respect buyer communication rules and comply with TAZY CART review and verification requirements.",
    cards: ["No prohibited products", "Accurate listings", "Verified account details", "Admin review when flagged"],
    cta: { label: "View categories", to: "/" },
  },
  "seller-verification": {
    eyebrow: "Seller verification",
    title: "Verification protects buyers and strengthens trust.",
    body: "Seller accounts can be required to complete profile details, selling categories and documentation before being allowed to trade publicly on the marketplace.",
    cards: ["Identity review", "Business documents", "Category focus", "Approval status tracking"],
    cta: { label: "Become a seller", to: "/register?type=SELLER" },
  },
  "order-protection": {
    eyebrow: "Order protection",
    title: "Orders, payments and fulfilment should be traceable.",
    body: "TAZY CART is structured to support order requests, seller confirmation, payment records, transporter selection and order progress tracking through the platform.",
    cards: ["Order tracking", "Payment records", "Seller confirmation", "Transporter updates"],
    cta: { label: "Start sourcing", to: "/request" },
  },
  "dispute-support": {
    eyebrow: "Support",
    title: "Disputes should be supported by records, not guesswork.",
    body: "Product, payment, enquiry, order and transporter activity records help platform administrators review issues more clearly when a dispute is raised.",
    cards: ["Order records", "Document history", "Delivery status", "Review notes"],
    cta: { label: "Contact support", to: "/contact" },
  },
  "buyer-help": {
    eyebrow: "Buyer Help",
    title: "Find products, contact sellers and track your orders.",
    body: "Buyers can browse categories, search products, send protected enquiries, place orders and follow progress inside TAZY CART.",
    cards: ["Search products", "Ask seller first", "Place order", "Track delivery"],
    cta: { label: "Browse products", to: "/" },
  },
  "seller-help": {
    eyebrow: "Seller Help",
    title: "Manage your store, products and buyer enquiries.",
    body: "Sellers can complete store setup, submit documents, manage their product catalogue, respond to buyer enquiries and coordinate fulfilment.",
    cards: ["Store profile", "Documents", "Product catalogue", "Buyer enquiries"],
    cta: { label: "Start selling", to: "/register?type=SELLER" },
  },
  "courier-help": {
    eyebrow: "Courier Help",
    title: "Showcase delivery services and manage delivery jobs.",
    body: "Transporters can create service listings, share delivery areas and pricing notes, receive delivery requests and update delivery progress.",
    cards: ["Service listings", "Delivery areas", "Assigned jobs", "Progress updates"],
    cta: { label: "Explore couriers", to: "/transporters" },
  },
  "contact": {
    eyebrow: "Contact TAZY CART",
    title: "Need marketplace assistance? Start from the right workflow.",
    body: "For product enquiries, contact the seller from the product page. For sourcing, use the request form. For platform support, use your account workspace when logged in.",
    cards: ["Product enquiries", "Sourcing requests", "Seller support", "Courier support"],
    cta: { label: "Open marketplace", to: "/" },
  },
  "privacy": {
    eyebrow: "Privacy Policy",
    title: "How TAZY CART handles your personal information.",
    body: "This policy explains what information we collect, why we use it, who may receive it and the choices and rights available to you.",
    effectiveDate: "Effective 7 September 2026",
    summary: [
      "We use personal information to operate the marketplace, process orders and payments, coordinate delivery, verify accounts, provide support and protect users.",
      "Your information is shared only where necessary for a transaction, service, legal obligation or security purpose. Promotional notifications can be switched off; essential transaction and security notices remain enabled.",
    ],
    sections: [
      {
        title: "1. Who this policy applies to",
        paragraphs: [
          "This Privacy Policy applies to buyers, sellers, business users, transporters, authorised staff and visitors who use the TAZY CART website, marketplace and account workspaces. In this policy, TAZY CART, we and us refer to the operator of the TAZY CART platform.",
          "TAZY CART handles personal information in accordance with applicable Zimbabwean law, including the Cyber and Data Protection Act [Chapter 12:07] and related regulations. The Postal and Telecommunications Regulatory Authority of Zimbabwe (POTRAZ) is the Data Protection Authority under that framework.",
        ],
      },
      {
        title: "2. Information we collect",
        bullets: [
          "Account and identity information, including your name, contact details, account type, login credentials and profile information.",
          "Seller, business and transporter verification information, including business details, service areas and documents submitted for approval.",
          "Marketplace activity, including listings, searches, enquiries, orders, reviews, disputes, saved details and communications made through the platform.",
          "Transaction information, including order values, payment method, payment reference and payment status. Wallet or card PINs and one-time passwords must only be entered into the authorised payment provider's process and must never be sent to TAZY CART staff or other users.",
          "Delivery information, including collection and delivery locations, recipient contact details, transporter selection, quotations and delivery status.",
          "Subscription, entitlement and usage information needed to apply package limits and provide included features.",
          "Device, browser, IP address, session, security and diagnostic information generated when the platform is used.",
        ],
      },
      {
        title: "3. Why we use personal information",
        paragraphs: ["We use information only for clear marketplace, contractual, legal, consent-based or legitimate operational purposes."],
        bullets: [
          "Creating and securing accounts and providing the correct role-based workspace.",
          "Publishing approved listings and enabling enquiries, orders, payments, settlements and delivery coordination.",
          "Verifying sellers, businesses and transporters and reviewing submitted documents.",
          "Providing customer support, resolving disputes, preventing fraud and enforcing marketplace rules.",
          "Operating subscriptions, usage limits, service messages and account alerts.",
          "Improving platform reliability and measuring service performance using proportionate operational information.",
          "Sending promotions or new-product alerts where you have not opted out and the communication is legally permitted.",
          "Meeting tax, accounting, regulatory, court-order and other lawful requirements.",
        ],
      },
      {
        title: "4. How information is shared",
        paragraphs: ["We do not sell your personal information. Information may be shared with the following recipients only to the extent reasonably required:"],
        bullets: [
          "A buyer, seller or business involved in the same enquiry, order, fulfilment or dispute.",
          "A selected transporter so that collection, delivery and recipient communication can be completed.",
          "Payment processors and financial service providers to initiate, verify or reconcile a payment. Their own privacy terms also apply to information handled directly by them.",
          "Technology, hosting, email, support, professional and security service providers acting under appropriate confidentiality and data-protection duties.",
          "POTRAZ, law-enforcement bodies, courts, regulators or other authorities where disclosure is required or lawfully requested.",
          "A successor or purchaser in a genuine business reorganisation, subject to lawful confidentiality and data-protection safeguards.",
        ],
      },
      {
        title: "5. Role-based access and marketplace visibility",
        paragraphs: [
          "TAZY CART restricts account data according to role and transaction involvement. Buyers see their own orders, payments and delivery tracking. Sellers and businesses see records connected to their own stores and transactions. Transporters see their services and assigned delivery work. Authorised operations or finance staff may access records required for approvals, reconciliation, support, fraud prevention and legal compliance.",
          "Public listings, seller profiles, transporter services and published reviews are intended to be visible to marketplace visitors. Do not place private information in a public listing or review.",
        ],
      },
      {
        title: "6. Notifications and marketing choices",
        paragraphs: [
          "Order confirmations, payment confirmations, delivery updates, account alerts, security notices and important service messages are essential to operating your account and cannot be disabled while the related service is in use.",
          "Promotional offers, marketing updates and new-product alerts are optional. Signed-in users can manage these choices in the Notification Centre. You may also use an unsubscribe method included in a marketing message where available.",
        ],
      },
      {
        title: "7. Storage, retention and transfers",
        paragraphs: [
          "We keep personal information only for as long as reasonably required for the purpose for which it was collected, to maintain transaction and security records, to resolve disputes and to meet applicable legal, tax, accounting or regulatory duties. When information is no longer required, it is deleted, de-identified or securely restricted as appropriate.",
          "If personal information is stored or processed outside Zimbabwe, TAZY CART will apply the approvals, contractual safeguards and other protections required by applicable law. We will not treat an overseas transfer as permission to reduce your protections.",
        ],
      },
      {
        title: "8. Security and data incidents",
        paragraphs: [
          "We use reasonable organisational and technical controls, including account authentication, access restrictions, protected document handling, activity records and service monitoring. No online service can promise absolute security, so users must protect their password, device and one-time payment codes and must notify us promptly of suspected misuse.",
          "Where a personal-data incident creates a legal notification duty, TAZY CART will investigate, contain and report it to the appropriate authority and affected persons in accordance with applicable law.",
        ],
      },
      {
        title: "9. Your rights",
        paragraphs: ["Subject to applicable law and any lawful exception, you may ask us to:"],
        bullets: [
          "Confirm whether we hold personal information about you and provide access to it.",
          "Correct information that is inaccurate, incomplete or out of date.",
          "Delete, restrict or stop certain processing where the law gives you that right.",
          "Object to direct marketing or withdraw a consent without affecting earlier lawful processing.",
          "Explain a significant decision based solely on automated processing, where applicable.",
          "Provide a reasonable route to raise a privacy complaint with TAZY CART and, if unresolved, with POTRAZ as the Data Protection Authority.",
        ],
      },
      {
        title: "10. Cookies and similar technology",
        paragraphs: [
          "The website may use necessary browser storage, cookies or similar technology to keep you signed in, remember security and shopping-flow information, preserve a form when sign-in is required and keep the service working. Optional analytics or marketing technology should be used only with the choices required by applicable law.",
        ],
      },
      {
        title: "11. Children",
        paragraphs: [
          "TAZY CART is intended for adults who can lawfully enter marketplace transactions. We do not knowingly invite a child to open a trading account or submit verification documents without appropriate parent or guardian involvement. Please contact us if you believe a child's information has been provided improperly.",
        ],
      },
      {
        title: "12. Changes and contact",
        paragraphs: [
          "We may update this policy when the platform, law or our processing practices change. Material changes will be communicated through the website, account workspace or another appropriate channel, and the effective date will be revised.",
          "To ask a privacy question, exercise a right or report a concern, use the TAZY CART Contact Support page or the support route in your account workspace. We may need to verify your identity before releasing or changing account information.",
        ],
      },
    ],
    cta: { label: "Back to marketplace", to: "/" },
  },
  "terms": {
    eyebrow: "Terms & Conditions",
    title: "The rules for using the TAZY CART marketplace.",
    body: "These terms define the responsibilities of buyers, sellers, businesses, transporters and platform users throughout a marketplace transaction.",
    effectiveDate: "Effective 7 September 2026",
    summary: [
      "By creating an account, placing an order, publishing a listing, submitting a delivery quote or otherwise using TAZY CART, you agree to these Terms & Conditions and the policies linked from them.",
      "Nothing in these terms removes a consumer or data-protection right that cannot lawfully be excluded under Zimbabwean law.",
    ],
    sections: [
      {
        title: "1. Eligibility and acceptance",
        paragraphs: [
          "You must be at least 18 years old and legally capable of entering a binding transaction, or use the platform through a duly authorised organisation or representative. By using TAZY CART, you confirm that the information you provide is truthful and that you have authority to act for any business you register.",
          "If you do not agree with these terms, do not create an account or use a transaction feature. Additional seller, transporter, subscription, payment or campaign terms shown before you accept a service form part of your agreement for that service.",
        ],
      },
      {
        title: "2. Accounts and security",
        bullets: [
          "Maintain one accurate account for your genuine role and keep profile and contact information current.",
          "Protect your password, device and authentication details and notify support promptly if an account may be compromised.",
          "Do not share one-time passwords, mobile-money PINs or card security details with a seller, transporter, buyer or TAZY CART staff member.",
          "You are responsible for activity performed through your account unless you reported unauthorised access promptly and the law provides otherwise.",
          "TAZY CART may request identity, business, address or service documents before enabling or continuing higher-risk marketplace functions.",
        ],
      },
      {
        title: "3. The role of TAZY CART",
        paragraphs: [
          "TAZY CART provides technology for product discovery, enquiries, ordering, payment records, delivery coordination, reviews, subscriptions and business operations. Unless a product or service is expressly identified as supplied by TAZY CART, the seller supplies the goods and the selected transporter supplies delivery services.",
          "TAZY CART may verify profiles, moderate content, keep transaction records, enforce workflow stages and assist with disputes, but verification is not a guarantee of every product, seller, buyer or delivery outcome.",
        ],
      },
      {
        title: "4. Seller and business responsibilities",
        bullets: [
          "List only lawful goods that you own or are authorised to sell and that comply with safety, labelling, tax, licensing and consumer requirements.",
          "Provide accurate descriptions, prices, stock status, images, delivery expectations and warranty or return information.",
          "Confirm or reject orders promptly, prepare the correct goods and keep the order workflow accurate.",
          "Protect buyer information and use it only for the related enquiry, order, delivery, support or legal requirement.",
          "Pay applicable marketplace commission, subscription charges, transporter charges and other clearly disclosed fees.",
          "Do not manipulate reviews, create deceptive listings, divert a platform order to avoid fees or sell prohibited, counterfeit, stolen or unsafe goods.",
        ],
      },
      {
        title: "5. Buyer responsibilities",
        bullets: [
          "Provide accurate recipient, contact, delivery and payment information.",
          "Review the listing, price, delivery quote, seller information and order summary before confirming payment.",
          "Be available for agreed delivery and inspect goods reasonably promptly.",
          "Confirm receipt only after the goods have been received. If there is a genuine problem, raise it through the available support or dispute route instead of falsely completing the order.",
          "Submit honest reviews based on a real marketplace transaction and do not use threats, harassment or unlawful content.",
        ],
      },
      {
        title: "6. Orders, prices and cancellations",
        paragraphs: [
          "An order request does not become final merely because it was submitted. The seller must confirm availability and the applicable payment step must be completed or accepted. TAZY CART may display a clear sequence such as seller confirmation, order confirmed, preparing goods, ready for delivery, in transit and buyer-confirmed completion.",
          "A user may not skip a protected fulfilment stage where an earlier confirmation, payment or handover is required. Cancellations, refunds and returns depend on the order stage, seller policy, product condition, applicable consumer law and any dispute decision. A confirmed payment does not by itself prove that goods were delivered.",
        ],
      },
      {
        title: "7. Payments and payment records",
        paragraphs: [
          "Available methods may include Paynow and the payment channels it supports, such as mobile money, as well as pay-on-delivery, bank transfer or other methods enabled for the transaction. To reduce confusion, a supported channel may be presented under one payment option. A payment remains pending until the relevant provider or an authorised reconciliation process confirms it.",
          "Users must verify the amount and recipient before approving a payment. TAZY CART may record payment code, order number, payer, beneficiary, method, reference, amount and status for fulfilment, settlement, support, fraud prevention and accounting. Provider availability and processing times are outside TAZY CART's direct control.",
          "Wallet, pending-balance, payout and escrow-style labels describe software workflow records. They do not represent TAZY CART's software developer as a bank, payment processor, escrow agent, financial institution, custodian of funds or regulator. Actual collection, holding, reversal, refund, chargeback and settlement depend on the selected payment provider, merchant account, applicable law and service availability.",
        ],
      },
      {
        title: "8. Commission, subscriptions and limits",
        paragraphs: [
          "Marketplace commission and subscription fees serve different purposes. Commission may be charged on a completed or confirmed marketplace sale at the rate shown in the applicable platform settings or agreement. A subscription provides access to a package of features and does not automatically replace transaction commission unless a package expressly says so.",
          "Each subscription may set limits for product listings, monthly enquiries, monthly assisted-search messages or other features. Your workspace should show the active package, included features, current use and remaining allowance. Features can be limited or paused when an allowance is exhausted, a subscription expires or payment remains outstanding. Package changes take effect according to the terms displayed when selected.",
          "Where offered, a first subscription trial is available once per account or verified business and uses the limits of the selected package. At the end of a trial or paid period, a displayed renewal grace period may keep existing listings and order fulfilment available while new listings and optional tools are paused. If renewal is not completed by the end of grace, public listings may be hidden without deleting transaction, wallet, payout or account records. A renewal reminder is not an automatic recurring charge.",
          "A proof-of-payment upload records a claim for review and does not activate access by itself. Cash, bank or other offline subscription payments must be verified by authorised TAZY CART staff. An activation code is single-use, time-limited and valid only for the account to which it was issued; sharing or attempting to reuse a code does not transfer access.",
        ],
      },
      {
        title: "9. Delivery and transporter payments",
        paragraphs: [
          "A transporter may provide a quote for a delivery request. The seller or business must accept the quote and complete the required transporter-payment step before pickup becomes available. A transporter payment marked pending is not treated as confirmed until the payment provider or authorised process confirms it.",
          "After confirmation, the transporter can accept or proceed with pickup, mark the parcel in transit and record delivery in the required sequence. The buyer then confirms receipt to complete the marketplace fulfilment process. Users must not submit a false status, and delivery evidence may be requested during a dispute.",
        ],
      },
      {
        title: "10. Reviews and marketplace content",
        paragraphs: [
          "Reviews must reflect a genuine eligible order or delivery and must be relevant, honest and respectful. Users may not post personal contact details, confidential information, discriminatory abuse, threats, spam, fabricated claims or content that infringes another person's rights.",
          "TAZY CART may investigate reports, request evidence, label verified transactions, allow an appropriate response and hide or remove content that violates these terms. Moderation does not require TAZY CART to adopt the reviewer's or respondent's opinion.",
        ],
      },
      {
        title: "11. Prohibited conduct",
        bullets: [
          "Fraud, impersonation, money laundering, unauthorised account access or payment manipulation.",
          "Malware, scraping that harms the service, automated abuse, interference with security or attempts to access another user's restricted information.",
          "Harassment, unlawful discrimination, deceptive advertising or misuse of buyer, seller or transporter contact information.",
          "Using the platform to trade illegal, stolen, counterfeit, dangerous, recalled or otherwise prohibited products.",
          "Circumventing order records, fees, verification, delivery controls or enforcement measures.",
        ],
      },
      {
        title: "12. Notifications and communications",
        paragraphs: [
          "You agree to receive essential order, payment, delivery, account, security and important platform messages connected to services you use. Promotional offers, marketing messages and new-product alerts can be controlled from the Notification Centre. Turning off marketing does not stop essential transaction or security communication.",
        ],
      },
      {
        title: "13. Intellectual property",
        paragraphs: [
          "TAZY CART branding, software, page design and platform materials are protected by applicable intellectual-property law. Users retain rights in content they lawfully own but grant TAZY CART a non-exclusive permission to host, display and process that content for marketplace operation, promotion of the relevant listing and enforcement of these terms. You must have the necessary rights to every image, description, logo and document you upload.",
        ],
      },
      {
        title: "14. Suspension and termination",
        paragraphs: [
          "TAZY CART may restrict, suspend or close an account or listing where reasonably necessary to investigate fraud, protect users, comply with law, address unpaid amounts, prevent harm or enforce these terms. Where appropriate, we will provide notice and a route to contact support. Immediate restriction may be used where delay would create legal, security or financial risk.",
        ],
      },
      {
        title: "15. Service availability and liability",
        paragraphs: [
          "We work to keep the platform reliable but do not guarantee uninterrupted availability, that every listing will result in a sale or that an independent user will perform perfectly. Users remain responsible for their products, business decisions and lawful obligations.",
          "To the extent permitted by law, TAZY CART is not responsible for indirect or consequential loss caused by an independent user's conduct or a third-party service outside our reasonable control. Nothing in these terms excludes liability or statutory rights that cannot lawfully be limited, including liability arising from fraud or other responsibility that the law does not permit us to exclude.",
        ],
      },
      {
        title: "16. Disputes, governing law and changes",
        paragraphs: [
          "Users should first preserve the order, payment and delivery records and raise the issue through TAZY CART support. The parties should cooperate in good faith and provide requested evidence. These terms are governed by the laws of Zimbabwe, and unresolved disputes may be referred to a competent Zimbabwean court or authority unless mandatory law requires another process.",
          "We may update these terms for legal, security, operational or service changes. Material changes will be communicated through an appropriate platform channel. Continued use after the effective date of revised terms constitutes acceptance where permitted by law; if you do not agree, you should stop using the affected service and contact support about account closure.",
        ],
      },
      {
        title: "17. Contact",
        paragraphs: [
          "For account, transaction, legal or policy questions, use the TAZY CART Contact Support page or the support route in your signed-in workspace. Include the relevant order, payment, delivery or account reference, but never include a password, wallet PIN or one-time password.",
        ],
      },
    ],
    cta: { label: "Marketplace rules", to: "/marketplace-rules" },
  },
};

export default function StaticContentPage({ slug }) {
  const params = useParams();
  const page = pages[slug || params.slug] || pages.about;

  if (page.sections) {
    return (
      <div className="page-shell marketplace-page" id="top">
        <MarketplaceHeader />
        <main className="static-public-main legal-public-main">
          <section className="static-public-hero legal-public-hero">
            <div className="container static-public-hero__inner">
              <div>
                <span className="eyebrow">{page.eyebrow}</span>
                <h1>{page.title}</h1>
                <p>{page.body}</p>
                <small>{page.effectiveDate}</small>
              </div>
            </div>
          </section>

          <section className="container legal-policy-layout">
            <aside className="legal-policy-nav" aria-label={`${page.eyebrow} sections`}>
              <strong>On this page</strong>
              {page.sections.map((section, index) => (
                <a key={section.title} href={`#legal-section-${index + 1}`}>{section.title}</a>
              ))}
            </aside>

            <div className="legal-policy-content">
              <div className="legal-policy-summary">
                {page.summary.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
              </div>
              {page.sections.map((section, index) => (
                <article id={`legal-section-${index + 1}`} key={section.title} className="legal-policy-section">
                  <h2>{section.title}</h2>
                  {(section.paragraphs || []).map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                  {section.bullets && (
                    <ul>{section.bullets.map((item) => <li key={item}>{item}</li>)}</ul>
                  )}
                </article>
              ))}
              <div className="legal-policy-actions">
                <Link to="/contact">Contact support</Link>
                <Link to={page.cta.to}>{page.cta.label}</Link>
              </div>
            </div>
          </section>
        </main>
        <Footer />
        <FloatingTools />
      </div>
    );
  }

  return (
    <div className="page-shell marketplace-page" id="top">
      <MarketplaceHeader />
      <main className="static-public-main">
        <section className="static-public-hero">
          <div className="container static-public-hero__inner">
            <div>
              <span className="eyebrow">{page.eyebrow}</span>
              <h1>{page.title}</h1>
              <p>{page.body}</p>
              <Link to={page.cta.to}>{page.cta.label}</Link>
            </div>
          </div>
        </section>
        <section className="container static-public-grid static-public-grid--clean">
          {page.cards.map((card, index) => (
            <article key={card}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <h3>{card}</h3>
              <p>{page.body}</p>
            </article>
          ))}
        </section>
        <section className="container static-public-note">
          <div>
            <span className="eyebrow">TAZY CART SUPPORT</span>
            <h2>Use the correct marketplace route for faster action.</h2>
            <p>
              Buyers should use product pages or sourcing requests. Sellers should use seller registration
              and verification. Couriers should use the transporter service route. Business users should start
              from business solutions.
            </p>
          </div>
          <Link to={page.cta.to}>{page.cta.label}</Link>
        </section>
      </main>
      <Footer />
      <FloatingTools />
    </div>
  );
}
